import { useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { CandidateDetail } from '@/modules/candidate-detail/components/CandidateDetail';
import { candidateBriefQueryKeys } from '@/modules/candidate-detail/queries/queryKeys';
import { useCandidateBriefQuery } from '@/modules/candidate-detail/queries/useCandidateBriefQuery';
import type {
  CandidateQueuePosition,
  CandidateTab,
  PendingDecisionAction,
} from '@/modules/candidate-detail/types';
import {
  decisionFromStatus,
  mapCandidateDetail,
  parseCandidateStatus,
} from '@/modules/candidate-detail/utils/candidateBrief';
import { isCandidateTab } from '@/modules/candidate-detail/utils/isCandidateTab';
import { useRejectCandidateJobMutation } from '@/modules/candidates/mutations/useRejectCandidateJobMutation';
import { HMLoadError } from '@/modules/hiring-manager/components/HMLoadError';
import {
  candidateSubmissionsAwaitingReviewQueryKeys,
  useCandidateSubmissionsAwaitingReviewQuery,
} from '@/modules/hiring-manager/queries/useCandidateSubmissionsAwaitingReviewQuery';
import { toSearchSuffix } from '@/modules/hiring-manager/utils/searchSuffix';
import { getHMTenantSelection } from '@/modules/hiring-manager/utils/tenant';
import { DocumentMetadata } from '@/modules/layouts/components/DocumentMetadata';
import { Skeleton } from '@/modules/ui/components/skeleton/Skeleton';
import { useCurrentUserQuery } from '@/modules/users/queries/useCurrentUserQuery';
import { actionErrorMessage } from '@/utils/actionErrorMessage';
import { invalidateQuery } from '@/utils/queries';

function CandidateDetailSkeleton() {
  return (
    <div aria-label="Loading candidate brief" className="flex flex-col gap-8">
      <span className="sr-only">Loading candidate brief</span>
      <div className="flex flex-col gap-4">
        <Skeleton className="h-4 w-40" />
        <div className="flex items-center gap-3">
          <Skeleton className="size-10 rounded-full" />
          <div className="flex flex-col gap-2">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-3.5 w-64" />
          </div>
        </div>
      </div>
      <div className="grid gap-10 lg:grid-cols-[240px_minmax(0,1fr)]">
        <Skeleton className="h-32" />
        <Skeleton className="h-64" />
      </div>
    </div>
  );
}

interface CandidateDetailContentProps {
  candidateId: string;
  jobId: string;
  queue?: CandidateQueuePosition;
  searchSuffix: string;
  selectedTab: CandidateTab;
  onTabChange: (tab: CandidateTab) => void;
}

function CandidateDetailContent({
  candidateId,
  jobId,
  queue,
  searchSuffix,
  selectedTab,
  onTabChange,
}: CandidateDetailContentProps) {
  const params = { jobId, candidateId };
  const briefQuery = useCandidateBriefQuery(params);
  const rejectMutation = useRejectCandidateJobMutation();
  const mutationParams = { jobId, candidateId };
  const pendingAction: PendingDecisionAction = rejectMutation.isPending
    ? 'reject'
    : null;

  const refreshCandidate = () =>
    Promise.all([
      briefQuery.refetch(),
      invalidateQuery(candidateBriefQueryKeys.touchpoints(params))(),
      invalidateQuery(candidateSubmissionsAwaitingReviewQueryKeys.all)(),
    ]);

  // The decision area updating is the confirmation; only a failure toasts.
  const mutationOptions = (action: string) => ({
    onSuccess: refreshCandidate,
    onError: (error: Error) => toast.error(actionErrorMessage(action, error)),
  });

  if (briefQuery.isPending) {
    return <CandidateDetailSkeleton />;
  }

  if (briefQuery.isError && briefQuery.data === undefined) {
    return (
      <HMLoadError
        description="We could not load this candidate profile. Check your connection and try again."
        isRetrying={briefQuery.isFetching}
        onRetry={() => void briefQuery.refetch()}
        title="Candidate profile unavailable"
      />
    );
  }

  if (briefQuery.data === undefined) {
    return <CandidateDetailSkeleton />;
  }

  const candidate = mapCandidateDetail(briefQuery.data);
  const status = parseCandidateStatus(briefQuery.data.status);
  // The reject call has succeeded but the brief refetch has not landed yet:
  // show the rejection now, so outreach on the header cannot reopen in the gap.
  // The page stays mounted across the decision queue, so match the candidate.
  const isRejectedHere =
    rejectMutation.isSuccess &&
    rejectMutation.variables.jobId === jobId &&
    rejectMutation.variables.candidateId === candidateId;
  const decision = isRejectedHere ? 'rejected' : decisionFromStatus(status);

  return (
    <>
      <DocumentMetadata
        description={`Review the candidate profile for ${candidate.name}.`}
        title={candidate.name}
      />
      {briefQuery.isError && (
        <div className="mb-6">
          <HMLoadError
            description="We could not refresh this candidate profile. Showing the previously loaded information."
            isRetrying={briefQuery.isFetching}
            onRetry={() => void briefQuery.refetch()}
            title="Candidate profile may be out of date"
          />
        </div>
      )}
      <CandidateDetail
        candidate={candidate}
        decision={decision}
        jobId={jobId}
        pendingAction={pendingAction}
        queue={queue}
        searchSuffix={searchSuffix}
        selectedTab={selectedTab}
        onReject={(rejectionMessage) => {
          rejectMutation.mutate(
            { ...mutationParams, message: rejectionMessage },
            mutationOptions('reject the candidate'),
          );
        }}
        status={status}
        onTabChange={onTabChange}
      />
    </>
  );
}

export function CandidateDetailPage() {
  const { jobId = '', candidateId = '' } = useParams<{
    jobId: string;
    candidateId: string;
  }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const selectedTab: CandidateTab = isCandidateTab(tabParam)
    ? tabParam
    : 'summary';

  // The decision queue: this job's candidates awaiting review, latest
  // submission first, the same order as the dashboard, so the position label
  // and Previous/Next match the list the user clicked through from.
  const currentUserQuery = useCurrentUserQuery();
  const tenantSelection = getHMTenantSelection(
    currentUserQuery.data,
    searchParams,
  );
  const awaitingReviewQuery = useCandidateSubmissionsAwaitingReviewQuery(
    tenantSelection.selectedTenantId,
  );
  const waiting = (awaitingReviewQuery.data ?? [])
    .filter((submission) => submission.jobId === jobId)
    .sort((a, b) => Date.parse(b.submittedAt) - Date.parse(a.submittedAt));
  const index = waiting.findIndex(
    (submission) => submission.candidateId === candidateId,
  );
  const toQueueEntry = (position: number) => {
    const submission = waiting[position];
    return submission
      ? { candidateId: submission.candidateId, name: submission.fullName }
      : undefined;
  };
  const queue: CandidateQueuePosition | undefined =
    index === -1
      ? undefined
      : {
          index,
          total: waiting.length,
          previous: toQueueEntry(index - 1),
          next: toQueueEntry(index + 1),
        };

  return (
    <CandidateDetailContent
      candidateId={candidateId}
      jobId={jobId}
      queue={queue}
      searchSuffix={toSearchSuffix(searchParams)}
      selectedTab={selectedTab}
      onTabChange={(tab) => {
        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.set('tab', tab);
        setSearchParams(nextSearchParams);
      }}
    />
  );
}
